import { useState, useRef, useEffect } from 'react';
import { Send, Bot, User } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { chatApi, handleApiError } from '@/services/api';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';

interface Source {
  document_id: number;
  filename: string;
  relevance_score: number;
  type?: string;
  confidence?: number;
  llm_score?: number;
  vector_score?: number;
}

interface Message {
  id: string;
  role: 'user' | 'assistant' | 'sources';
  content: string;
  timestamp: Date;
  sources?: Source[];
}

interface ChatSidebarProps {
  projectId: number;
  selectedModel?: string;  // LLM model selected in UI
  onClose?: () => void;
}

export function ChatSidebar({ projectId, selectedModel, onClose }: ChatSidebarProps) {
  const STORAGE_KEY = `chat-history-${projectId}`;
  const MAX_HISTORY = 10; // Keep last 10 messages
  
  // Load chat history from localStorage
  const loadChatHistory = (): Message[] => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        // Convert timestamp strings back to Date objects
        return parsed.map((msg: any) => ({
          ...msg,
          timestamp: new Date(msg.timestamp)
        }));
      }
    } catch (error) {
      console.error('Failed to load chat history:', error);
    }
    return [];
  };
  
  const [messages, setMessages] = useState<Message[]>(loadChatHistory());
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  
  // Settings
  const [useDirectFiles, setUseDirectFiles] = useState(false);
  
  // Map UI model names to API model names
  const getApiModel = (uiModel?: string): string => {
    if (!uiModel) return 'ollama';
    if (uiModel === 'claude') return 'claude';
    if (uiModel === 'openai') return 'openai';
    return 'ollama';  // Default for ollama-8b, ollama-14b, etc.
  };
  
  // Get user-friendly display name for model
  const getModelDisplayName = (uiModel?: string): string => {
    if (!uiModel || uiModel.includes('ollama')) return 'Local Model';
    if (uiModel === 'claude') return 'Remote Model';
    return 'Remote Model';
  };
  
  const isCloudModel = selectedModel === 'claude';
  
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    scrollToBottom();
    
    // Save chat history to localStorage (keep last 10 messages)
    if (messages.length > 0) {
      try {
        const recentMessages = messages.slice(-MAX_HISTORY);
        localStorage.setItem(STORAGE_KEY, JSON.stringify(recentMessages));
      } catch (error) {
        console.error('Failed to save chat history:', error);
      }
    }
  }, [messages, STORAGE_KEY, MAX_HISTORY]);

  const scrollToBottom = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  };

  const handleSend = async () => {
    if (!input.trim() || loading) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: input,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    const questionText = input;
    setInput('');
    setLoading(true);

    // Add progress message with ETA based on model
    const progressMessageId = (Date.now() + 1).toString();
    const isLocalModel = selectedModel?.includes('ollama') || false;
    const etaMessage = isLocalModel 
      ? '⏳ Processing with local model... ETA: 2-3 minutes\n\nStage 1: Retrieving documents...'
      : '⏳ Processing with external API... ETA: 20-30 seconds\n\nStage 1: Retrieving documents...';
    
    const progressMessage: Message = {
      id: progressMessageId,
      role: 'assistant',
      content: etaMessage,
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, progressMessage]);

    try {
      // Use regular endpoint for ALL models (no SSE - eliminates timeouts)
      const response = await chatApi.ask({
        project_id: projectId,
        question: questionText,
        use_direct_files: useDirectFiles,
        llm_model: selectedModel || 'ollama-14b',
      });

      // Remove progress message
      setMessages((prev) => prev.filter(m => m.id !== progressMessageId));

      // Add sources if available
      if (response.sources && response.sources.length > 0) {
        const sourcesMessage: Message = {
          id: (Date.now() + 2).toString(),
          role: 'sources',
          content: `Found ${response.sources.length} relevant documents`,
          timestamp: new Date(),
          sources: response.sources,
        };
        setMessages((prev) => [...prev, sourcesMessage]);
      }

      // Add assistant response
      const assistantMessage: Message = {
        id: (Date.now() + 3).toString(),
        role: 'assistant',
        content: response.answer,
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, assistantMessage]);
      setLoading(false);
      inputRef.current?.focus();

    } catch (error) {
      toast.error(handleApiError(error));
      setMessages((prev) => prev.filter(m => m.id !== progressMessageId));
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: 'Sorry, I encountered an error processing your question.',
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, errorMessage]);
      setLoading(false);
      inputRef.current?.focus();
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="flex flex-col h-full bg-background border-l">
      {/* Header */}
      <div className="p-4 border-b">
        <div className="flex items-center gap-2 mb-4">
          <Bot className="h-5 w-5" />
          <h2 className="font-semibold">Document Q&A</h2>
        </div>

        {/* Settings */}
        <div className="space-y-3 pt-3 border-t mt-8">
          <div className="flex items-center space-x-2">
            <Checkbox
              id="direct-files"
              checked={useDirectFiles}
              onCheckedChange={(checked: boolean) => setUseDirectFiles(checked)}
            />
            <Label htmlFor="direct-files" className="text-sm cursor-pointer">
              Query files directly (vs annotations)
            </Label>
          </div>
          <div className="text-xs text-muted-foreground space-y-1">
            <p>
              {useDirectFiles
                ? '📄 Analyzing document content directly'
                : '⚡ Using extracted annotations (faster)'}
            </p>
            <p className="mt-1 text-xs font-medium">
              Model: {getModelDisplayName(selectedModel)}
            </p>
          </div>
        </div>
      </div>

      {/* Messages */}
      <ScrollArea ref={scrollRef} className="flex-1 p-4">
        {messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center text-muted-foreground">
            <Bot className="h-12 w-12 mb-4 opacity-50" />
            <p className="text-sm">Ask questions about your documents</p>
            <p className="text-xs mt-2">
              Using {useDirectFiles ? 'direct files' : 'annotations'} with {isCloudModel ? 'cloud' : 'local'} model
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {messages.map((message) => (
              <div
                key={message.id}
                className={`flex gap-3 w-full ${
                  message.role === 'user' ? 'justify-end' : 'justify-start'
                }`}
              >
                {(message.role === 'assistant' || message.role === 'sources') && (
                  <div className="flex-shrink-0">
                    <Bot className="h-6 w-6 text-primary" />
                  </div>
                )}
                <div
                  className={`max-w-[85%] min-w-0 rounded-lg p-3 overflow-hidden break-words ${
                    message.role === 'user'
                      ? 'bg-primary text-primary-foreground'
                      : message.role === 'sources'
                      ? 'bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800'
                      : 'bg-muted'
                  }`}
                >
                  {/* Sources message with detailed breakdown */}
                  {message.role === 'sources' && message.sources && message.sources.length > 0 ? (
                    <div className="break-words">
                      <p className="text-sm font-semibold mb-3 text-blue-900 dark:text-blue-100 break-words">
                        🔍 {message.content}
                      </p>
                      <div className="space-y-2">
                        {message.sources.map((source, idx) => {
                          // Determine which search method found this document
                          const llmHigh = (source.llm_score || 0) > 0.5;
                          const vectorHigh = (source.vector_score || 0) > 0.5;
                          let methodBadge = '';
                          
                          if (llmHigh && vectorHigh) {
                            methodBadge = '🤖📊 LLM + Vector';
                          } else if (llmHigh) {
                            methodBadge = '🤖 LLM-based';
                          } else if (vectorHigh) {
                            methodBadge = '📊 Vector';
                          } else {
                            methodBadge = '⚠️ Low Match';
                          }
                          
                          return (
                            <div key={idx} className="text-xs bg-white dark:bg-gray-900 rounded p-2 border border-blue-100 dark:border-blue-900 break-words overflow-hidden">
                              <div className="flex items-start justify-between gap-2 mb-1">
                                <span className="font-medium break-words overflow-wrap-anywhere flex-1 min-w-0">📄 {source.filename}</span>
                                <span className="font-bold text-blue-600 dark:text-blue-400 flex-shrink-0">
                                  {(source.relevance_score * 100).toFixed(0)}%
                                </span>
                              </div>
                              <div className="flex items-center justify-between gap-2 flex-wrap">
                                <div className="flex gap-3 text-[10px] text-muted-foreground flex-wrap">
                                  <span className="whitespace-nowrap">🤖 LLM: {(source.llm_score! * 100).toFixed(0)}%</span>
                                  <span className="whitespace-nowrap">📊 Vector: {(source.vector_score! * 100).toFixed(0)}%</span>
                                </div>
                                <span className="text-[10px] px-2 py-0.5 rounded bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 font-semibold whitespace-nowrap">
                                  {methodBadge}
                                </span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ) : (
                    <div className="text-sm break-words max-w-full overflow-hidden prose prose-sm dark:prose-invert max-w-none" style={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}>
                      <ReactMarkdown>
                        {message.content}
                      </ReactMarkdown>
                    </div>
                  )}
                  
                  <p className="text-xs opacity-70 mt-2">
                    {message.timestamp.toLocaleTimeString()}
                  </p>
                </div>
                {message.role === 'user' && (
                  <div className="flex-shrink-0">
                    <User className="h-6 w-6" />
                  </div>
                )}
              </div>
            ))}
            {loading && (
              <div className="flex gap-3">
                <div className="flex-shrink-0">
                  <Bot className="h-6 w-6 text-primary" />
                </div>
                <div className="bg-muted rounded-lg p-3">
                  <div className="flex gap-1">
                    <div className="w-2 h-2 bg-current rounded-full animate-bounce" />
                    <div className="w-2 h-2 bg-current rounded-full animate-bounce delay-75" />
                    <div className="w-2 h-2 bg-current rounded-full animate-bounce delay-150" />
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </ScrollArea>

      {/* Input */}
      <div className="p-4 border-t">
        <div className="flex gap-2">
          <Input
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Ask about your documents..."
            disabled={loading}
            className="flex-1"
          />
          <Button onClick={handleSend} disabled={loading || !input.trim()}>
            <Send className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
